import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Lock, Unlock, Trash2, ArrowLeft, MessageSquare, Pencil, MapPin } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (n) => "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const FILE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
const API = process.env.REACT_APP_BACKEND_URL;

export const RapBadge = ({ s }) => s === "trasmesso"
  ? <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700"><Lock className="mr-1 h-3 w-3" />Trasmesso</Badge>
  : <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">Bozza</Badge>;

const DetailTable = ({ testid, rows, empty, headCol, unitLabel, qtyLabel, totalColor, totalValue }) => {
  if (!rows.length) return <p className="text-sm text-slate-400">{empty}</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm" data-testid={testid}>
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2 text-left font-semibold">{headCol}</th>
            <th className="px-3 py-2 text-right font-semibold">{unitLabel}</th>
            <th className="px-3 py-2 text-right font-semibold">{qtyLabel}</th>
            <th className="px-3 py-2 text-right font-semibold">Totale</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0">
              <td className="px-3 py-2 text-slate-800">{r.label}</td>
              <td className="px-3 py-2 text-right text-slate-600">{r.unit}</td>
              <td className="px-3 py-2 text-right text-slate-600">{r.qty}</td>
              <td className="px-3 py-2 text-right font-medium text-slate-900">{r.total}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-slate-200 bg-slate-50">
            <td className="px-3 py-2 text-xs font-semibold uppercase text-slate-500" colSpan={3}>Totale</td>
            <td className={`px-3 py-2 text-right font-heading font-bold ${totalColor}`}>{fmtEur(totalValue)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export function RapportinoDetailDialog({ detail, open, onClose, onChanged, backLabel = "Torna indietro" }) {
  const navigate = useNavigate();
  const done = () => { onChanged && onChanged(); };

  const unlock = async (id) => { try { await api.post(`/rapportini/${id}/unlock`); toast.success("Rapportino sbloccato"); onClose(); done(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const checkinUnlock = async (id) => { try { await api.post(`/rapportini/${id}/checkin-unlock`); toast.success("Check-in sbloccato"); onClose(); done(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const remove = async (id) => { try { await api.delete(`/rapportini/${id}`); toast.success("Eliminato"); onClose(); done(); } catch (e) { toast.error("Errore"); } };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="rap-detail-dialog">
        <DialogHeader>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="rap-detail-back" className="mb-1 -ml-2 w-fit rounded-md text-slate-500">
            <ArrowLeft className="mr-1.5 h-4 w-4" /> {backLabel}
          </Button>
          <DialogTitle className="font-heading">Rapportino · {detail?.commessa_name}</DialogTitle>
          <DialogDescription>{detail?.operator_name} · {fmtDate(detail?.date)} · {detail?.hours} h{detail?.hour_type_name ? ` · ${detail.hour_type_name}` : ""} · <RapBadge s={detail?.status} /></DialogDescription>
        </DialogHeader>
        {detail && (
          <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
            {detail.edit_requested && (
              <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-[#002FA7]" data-testid="rap-detail-edit-request">
                <MessageSquare className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-semibold">L'operatore ha richiesto la modifica di questo rapportino.</p>
                  {detail.edit_request_note && <p className="mt-0.5 text-slate-600">Nota: {detail.edit_request_note}</p>}
                  <p className="mt-0.5 text-xs text-slate-500">Sblocca il rapportino per consentire la modifica.</p>
                </div>
              </div>
            )}
            {detail.last_edited_by && (
              <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="rap-detail-edit-log">
                <Pencil className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                <div>
                  <p className="font-semibold text-slate-800">Corretto da {detail.last_edited_by} · {fmtDateTime(detail.last_edited_at)}</p>
                  {(detail.edit_history || []).length > 1 && (
                    <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
                      {[...detail.edit_history].reverse().slice(1).map((h, i) => <li key={i}>{h.by} · {fmtDateTime(h.at)}</li>)}
                    </ul>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3"><p className="text-xs uppercase text-slate-400">Ricavi</p><p className="font-heading text-xl font-extrabold text-[#002FA7]" data-testid="rap-detail-revenue">{fmtEur(detail.revenue)}</p></div>
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3"><p className="text-xs uppercase text-slate-400">Costi</p><p className="font-heading text-xl font-extrabold text-[#002FA7]" data-testid="rap-detail-cost">{fmtEur(detail.cost_total)}</p></div>
              <div className={`rounded-lg border p-3 ${detail.margin < 0 ? "border-rose-100 bg-rose-50/50" : "border-emerald-100 bg-emerald-50/50"}`}><p className="text-xs uppercase text-slate-400">Margine</p><p className={`font-heading text-xl font-extrabold ${detail.margin < 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="rap-detail-margin">{fmtEur(detail.margin)}</p></div>
            </div>

            <Tabs defaultValue="produzione" key={detail.id}>
              <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-4 lg:grid-cols-7">
                <TabsTrigger value="produzione" data-testid="tab-produzione">Produzione</TabsTrigger>
                <TabsTrigger value="personale" data-testid="tab-personale">Costi personale</TabsTrigger>
                <TabsTrigger value="mezzi" data-testid="tab-mezzi">Costi mezzi</TabsTrigger>
                <TabsTrigger value="materiali" data-testid="tab-materiali">Costi materiali</TabsTrigger>
                <TabsTrigger value="carburante" data-testid="tab-carburante">Costi carburante</TabsTrigger>
                <TabsTrigger value="checkin" data-testid="tab-checkin">Check-in</TabsTrigger>
                <TabsTrigger value="notefoto" data-testid="tab-notefoto">Note e foto</TabsTrigger>
              </TabsList>

              <TabsContent value="produzione" data-testid="tabc-produzione" className="pt-3">
                <DetailTable testid="prod-table" empty="Nessuna voce di produzione." headCol="Voce" unitLabel="Prezzo unit." qtyLabel="Quantità" totalColor="text-emerald-600" totalValue={detail.revenue}
                  rows={detail.vdc.map((v) => ({ label: <span>{v.description}{v.notes ? <span className="block text-xs italic text-slate-400">{v.notes}</span> : null}</span>, unit: fmtEur(v.unit_price), qty: `${v.quantity} ${v.unit || ""}`.trim(), total: fmtEur(v.total) }))} />
              </TabsContent>

              <TabsContent value="personale" data-testid="tabc-personale" className="pt-3">
                <p className="mb-2 text-xs text-slate-500">Tipo ore: {detail.hour_type_name || "—"} · Ore: {detail.hours} h</p>
                <DetailTable testid="pers-table" empty="Nessun componente squadra." headCol="Componente" unitLabel="Costo orario" qtyLabel="Ore" totalColor="text-rose-600" totalValue={detail.cost_persons}
                  rows={detail.team.map((t) => ({ label: t.name, unit: `${fmtEur(t.cost_per_hour)}/h`, qty: `${detail.hours} h`, total: fmtEur(t.cost) }))} />
              </TabsContent>

              <TabsContent value="mezzi" data-testid="tabc-mezzi" className="pt-3">
                <p className="mb-2 text-xs text-slate-500">Ore: {detail.hours} h</p>
                <DetailTable testid="mezzi-table" empty="Nessun mezzo." headCol="Mezzo" unitLabel="Costo orario" qtyLabel="Ore" totalColor="text-rose-600" totalValue={detail.cost_vehicles}
                  rows={detail.vehicles.map((v) => ({ label: `${v.name}${v.plate ? ` (${v.plate})` : ""}`, unit: `${fmtEur(v.cost_per_hour)}/h`, qty: `${detail.hours} h`, total: fmtEur(v.cost) }))} />
              </TabsContent>

              <TabsContent value="materiali" data-testid="tabc-materiali" className="pt-3">
                <DetailTable testid="mat-table" empty="Nessun materiale." headCol="Materiale" unitLabel="Costo unit." qtyLabel="Quantità" totalColor="text-rose-600" totalValue={detail.cost_materials}
                  rows={detail.materials.map((m) => ({ label: m.name, unit: fmtEur(m.unit_cost), qty: `${m.quantity} ${m.unit || ""}`.trim(), total: fmtEur(m.cost) }))} />
              </TabsContent>

              <TabsContent value="carburante" data-testid="tabc-carburante" className="pt-3">
                <DetailTable testid="fuel-table" empty="Nessun carburante autorizzato per questa giornata." headCol="Mezzo" unitLabel="Autorizzato" qtyLabel="Effettivo" totalColor="text-rose-600" totalValue={detail.cost_fuel}
                  rows={(detail.fuel_lines || []).map((f) => ({ label: <span>{f.employee_name || "—"}{(f.vehicle_name || f.vehicle_plate || f.fuel_card) ? <span className="block text-xs text-slate-400">{[f.vehicle_name, f.vehicle_plate].filter(Boolean).join(" · ")}{f.fuel_card ? `${(f.vehicle_name || f.vehicle_plate) ? " · " : ""}Carta ${f.fuel_card}` : ""}</span> : null}</span>, unit: fmtEur(f.authorized), qty: f.actual != null ? fmtEur(f.actual) : "—", total: fmtEur(f.cost) }))} />
              </TabsContent>

              <TabsContent value="checkin" data-testid="tabc-checkin" className="pt-3">
                {!detail.checkin ? <p className="text-sm text-slate-400">Nessun check-in registrato per questa giornata.</p> : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <p className="text-xs text-slate-500">Check-in del {fmtDateTime(detail.checkin.checked_in_at)}</p>
                        {detail.checkin.location?.lat != null && (
                          <a href={`https://www.google.com/maps?q=${detail.checkin.location.lat},${detail.checkin.location.lng}`} target="_blank" rel="noreferrer" data-testid="checkin-location-link" className="flex items-center gap-1 text-xs font-medium text-[#002FA7] hover:underline">
                            <MapPin className="h-3.5 w-3.5" /> Posizione check-in
                          </a>
                        )}
                      </div>
                      {detail.checkin.unlock_requested && <Button size="sm" onClick={() => checkinUnlock(detail.id)} data-testid="checkin-admin-unlock" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"><Unlock className="mr-1.5 h-4 w-4" />Sblocca check-in</Button>}
                    </div>
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Dispositivi e attrezzature</p>
                      {(detail.checkin.equipment || []).length === 0 ? <p className="text-sm text-slate-400">—</p> : (detail.checkin.equipment || []).map((e, i) => (
                        <div key={i} className="flex items-center justify-between border-b border-slate-100 py-1 text-sm">
                          <span className="text-slate-800">{e.name}{e.note ? <span className="text-xs italic text-slate-400"> · {e.note}</span> : null}</span>
                          {e.present ? <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Presente</Badge> : <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">Assente</Badge>}
                        </div>
                      ))}
                    </div>
                    <div className="space-y-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Stato mezzi</p>
                      {(detail.checkin.vehicle_conditions || []).map((vc, i) => (
                        <div key={i} className="rounded-lg border border-slate-200 p-3">
                          <p className="mb-2 text-sm font-semibold text-slate-800">{vc.name}</p>
                          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                            {(vc.photos || []).map((p, j) => (
                              <a key={j} href={FILE_BASE + p.path} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-md border border-slate-200">
                                <img src={FILE_BASE + p.path} alt={p.label} className="aspect-square w-full object-cover" />
                                <span className="block bg-slate-800 py-0.5 text-center text-[10px] text-white">{p.label}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="notefoto" data-testid="tabc-notefoto" className="pt-3">
                <p className="mb-1 text-xs font-semibold uppercase text-slate-400">Note</p>
                <p className="mb-3 whitespace-pre-wrap text-sm text-slate-700">{detail.notes || <span className="text-slate-400">Nessuna nota.</span>}</p>
                <p className="mb-1 text-xs font-semibold uppercase text-slate-400">Foto</p>
                {detail.photos && detail.photos.length ? (
                  <div className="flex flex-wrap gap-2" data-testid="rap-detail-photos">
                    {detail.photos.map((p, i) => <a key={i} href={`${API}/api/files/${p}`} target="_blank" rel="noreferrer"><img src={`${API}/api/files/${p}`} alt="foto" className="h-24 w-24 rounded-md object-cover" /></a>)}
                  </div>
                ) : <p className="text-sm text-slate-400">Nessuna foto caricata.</p>}
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => remove(detail.id)} data-testid="rap-detail-delete" className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 className="mr-2 h-4 w-4" /> Elimina</Button>
              <Button variant="outline" onClick={() => navigate(`/rapportino/${detail.id}`)} data-testid="rap-detail-edit" className="rounded-md"><Pencil className="mr-2 h-4 w-4" /> Modifica</Button>
              {detail.status === "trasmesso" && <Button onClick={() => unlock(detail.id)} data-testid="rap-detail-unlock" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"><Unlock className="mr-2 h-4 w-4" /> Sblocca per operatore</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
